import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useMergeAppointmentGapsWarning } from "./shared/useMergeAppointmentGapsWarning";
import { mergeGroupPatientIntoContinuousTherapistAppointments } from "./shared/continuousGroupMerge";
import { isNoOpEventChange } from "./shared/eventChangeHelpers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import {
  AppointmentDto,
  TimeSlotDto,
  LeaveOfAbsenceDto,
  EventTimeSlotDto,
  ContinuousEventTimeSlotDto,
  ContinuousAppointmentDto,
  SimpleWorkingHoursDto,
  PatientAvailabilityDto,
  PatientDto,
  UserDto,
  SublocalityDto,
  WorkingHoursDto,
  WorkHourExceptionDtoTypeEnum,
  WorkHourExceptionDtoInitiatorEnum,
  WeeklyWorkDayDto,
} from "../../../api-client";
import { useLocationId } from "../../../stores/User/User.hooks";
import {
  useUsersByLocation,
  useAppointmentsForDate,
  useContinuousAppointmentsByDayAndLocation,
  useContinuousAppointmentsForUser,
  usePatientUnavailabilityForCalendar,
  usePatientUnavailabilityTypeByDateForCalendar,
  useCachedAppointmentLookup,
  useSublocalitiesForAppointmentsAddresses,
  useUserIdsByDateAndRole,
  useDerivedWorkingHours,
} from "../../../stores/Calendar/calendar.queries";
import { calendarKeys, invalidateAllCalendarQueries, invalidateCalendarAppointmentCaches } from "../../../stores/Calendar/calendar.keys";
import { appointmentKeys } from "../../../stores/Appointment/appointment.keys";
import useStore from "../../../helpers/useStore";
import { CalendarContext } from "../../../stores/Calendar/calendar.provider";
// import { Event, TimeSlotExt } from "../Calendar.type";
import { ClientTimeSlot, EventType } from "./adapters/types";
import { CalendarEvent, adaptCalendarEventToUpdateData, isCancelledAppointment, isGroupPatientType, isGroupTherapistType } from "./adapters";
import { toCalendarEvents, toCalendarResources } from "./CalendarAdapters";
import notificationStore from "../../../stores/Notification/notificationStore";
import { useSocketHandler } from "./SocketHandler";
import { EventReceiveArg } from "@fullcalendar/interaction";
import { updateReceivedAppointment } from "../../../helpers/calendar";
import {
  useSaveAppointment,
  useUpdateContinuousTimeSlot,
  useUpdateTimeSlot,
  usePatchContinuousTimeSlotTimesAndUser,
  usePatchTimeSlotTimesAndUser,
  useScheduleUnscheduledTimeSlot,
  useDeleteLunchBreakForDay,
  useDeleteWorkHourException,
  useCreateWorkingHoursForDay,
  useDeleteGroupTherapist,
  useDeleteTimeSlot,
  useDeleteContinuousTimeSlot,
  useUserAbsence,
} from "../../../stores/Calendar/calendar.mutations";
import { useGuardedWorkHourExceptionMutations } from "../lunchBreak/useGuardedWorkHourExceptionMutations";
import { useUpdateContinuousAppointment } from "../../../stores/Appointment/appointment.mutation";
import { useCreateAndScheduleAppointment } from "../../../stores/Appointment/appointment.mutation";
import {
  eventChangeToAppointmentUpdateData,
  eventChangeToTimeSlotDto,
  eventChangeToContinuousAppointmentUpdateData,
  eventToClientLunchBreak,
  eventToClientContinuousLunchBreak,
} from "./adapters/display-to-client/reverse.adapter";
import { EventChangeArg } from "@fullcalendar/core";
import { useEventShelfController } from "../EventShelf/EventShelfController";
import { CalendarResource } from "./adapters/types";
import { useSelectedUsers } from "./useSelectedUsers";
import { useDeleteWeeklyWorkDay, useUpdateWeeklyWorkDay } from "../../../stores/User/user.mutations";
import useCalendarValidations from "../Calendar.validations";
import horizonConfirmStore from "../../../stores/Calendar/horizonConfirmStore";

// Stable empty collection singletons to avoid new array identities each render
const EMPTY_LOCKED_APPOINTMENTS: string[] = [];
const EMPTY_SUBLOCALITIES: SublocalityDto[] = [];

// Utility: returns previous array reference if shallow-equal (by ===) to avoid identity churn
function useShallowStableArray<T>(value: T[] | undefined): T[] {
  const ref = useRef<T[]>(value || []);
  const incoming = value || [];
  if (ref.current.length === incoming.length) {
    let same = true;
    for (let i = 0; i < incoming.length; i++) {
      if (ref.current[i] !== incoming[i]) {
        same = false;
        break;
      }
    }
    if (same) {
      // console.debug(
      //   "[useShallowStableArray] No change in array, reusing previous reference",
      //   value
      // );
      return ref.current;
    }
  }
  ref.current = incoming;
  // console.debug("[useShallowStableArray] Array changed, updating reference", value);
  return ref.current;
}

export interface CalendarData {
  // Core calendar data
  appointments: AppointmentDto[];
  leaveOfAbsences: LeaveOfAbsenceDto[];
  eventTimeslots: EventTimeSlotDto[];
  lunchBreaks: WorkingHoursDto[];
  travelTimes: TimeSlotDto[];
  // Flattened calendar events (adapter output) - transitional during refactor
  calendarEvents: CalendarEvent[];

  // Users and resources
  users: UserDto[];
  resources: CalendarResource[];

  // Patient and drawer data
  selectedShelfAppointment?: AppointmentDto;
  patientAvailabilities: PatientAvailabilityDto[];
  patientUnavailableTypes: TimeSlotDto[];
  patient?: PatientDto;
  patientAppointments?: AppointmentDto[];
  unscheduleds: AppointmentDto[];
  shelfAppointments: AppointmentDto[];
  shelfContinuousAppointments: ContinuousAppointmentDto[];

  // UI state
  isLoading: boolean;
  selectedDate: Date;
  selectedDateStr: string;
  isContinuousView: boolean;
  isCancelledView: boolean;
  patientDrawerOpen: boolean;
  selectedEventRxId?: string;
  showOpenDrawerButton: boolean;
  lockedAppointments: string[];
  sublocalities: SublocalityDto[];
  headerWidth: string;
  groupCheck: boolean;
  // Focus position for keyboard navigation persistence
  focusPosition: { resourceId: string; time: string } | null;
}

export interface CalendarHandlers {
  // Date and view handlers
  onDatesSet: (date: Date) => void;
  onContinuousViewChange: (continuous: boolean) => void;
  onCancelledViewChange: (checked: boolean) => void;

  // Event handlers - using new CalendarEvent type
  onCreateEvent: (event: CalendarEvent) => Promise<void>; // Keep as Event for now (creation uses old interface)
  onChangeEvent: (eventChangeInfo: EventChangeArg) => Promise<void>;
  onEventReceive: (eventReceive: EventReceiveArg) => Promise<void>;
  onRemoveEvent: (id: string) => Promise<void>;
  onRemoveContinuousEvent: (id: string) => Promise<void>;
  onRemoveGroupTherapist: (id: string) => Promise<void>;
  onMoveEventDate: (event: CalendarEvent) => Promise<void>;
  onAddShelfEvent: (event: CalendarEvent) => Promise<void>; // Keep as Event for now
  onEventContentClick: (event: ClientTimeSlot) => Promise<void>;
  onSelectedShelfEventReceive: (appointment: AppointmentDto) => Promise<void>;

  // Patient handlers
  onPatientTabChange: (newPatientId: string) => Promise<void>;
  onTargetPatientChange: (newTarget: PatientDto) => Promise<void>;
  onCloseNotesDialog: () => void;
  onSelectShelfAppointment: (appId: string) => void;
  onDataRefreshFlag: () => Promise<void>;

  // Utility handlers
  onOutdated: () => void;
  renderCalendar: () => Promise<void>;
  // Infinite-scroll window extension (single-therapist view only). Optional because the
  // main calendar uses a fixed day range and does not extend on horizontal scroll.
  onReachWindowStart?: () => void;
  onReachWindowEnd?: () => void;
}

export const useCalendarController = (): {
  data: CalendarData;
  handlers: CalendarHandlers;
} | null => {
  // S T O R E S   A N D   U I   S T A T E  ******************************************************************
  // URL and location context
  const locationId = useLocationId();
  const queryClient = useQueryClient();

  // Store contexts
  const CalendarStore = useStore(CalendarContext);

  // Calendar store state
  const {
    selectedDateStr,
    isCancelledView,
    setCancelledView,
    selectedPatientId,
    setTargetPatientId,
    patientDrawerOpen,
    continuousView,
    columnUserIds,
    createTimeslot,
    createEventTimeSlot,
    createContinuousEventTimeSlot,
    createLeaveOfAbsence,
    groupCheck,
    pinBoardItems,
    isRoleMode,
    roleForUserList,
    focusPosition,
  } = CalendarStore;

  const { mutateAsync: deleteGroupTherapist } = useDeleteGroupTherapist();
  const { mutateAsync: deleteTimeslot } = useDeleteTimeSlot();
  const { mutateAsync: deleteContinuousTimeslot } = useDeleteContinuousTimeSlot();
  const { mutateAsync: userAbsence } = useUserAbsence();

  // Visit-gap warning, shared with the single-therapist calendar controller.
  // The dialog itself is hosted shell-wide by CalendarActionDialogs.
  const { showMergeGapWarningIfPresent, showVisitGapWarningIfPresent } =
    useMergeAppointmentGapsWarning();
  const updateWeeklyWorkDayMutation = useUpdateWeeklyWorkDay();


  // Appointment store
  // removed: const { updateContinuousAppointment } = AppointmentStore;

  // Initialize WebSocket event handlers for real-time updates
  useSocketHandler();

  const { handleAddToPinBoard, handleRemoveFromPinBoard } = useEventShelfController();

  const { lookupByAppointmentId } = useCachedAppointmentLookup();

  // Derived state
  // Memoize date object so identity only changes when the string changes
  const selectedDate = useMemo(() => new Date(selectedDateStr), [selectedDateStr]);
  const weeklyWorkDay = true;

  // D A T A  ( Q U E R I E S )  ******************************************************************
  // React Query hooks

  const { userIds, fetchedUsers, derivedWorkingHours, continuousWorkPlansForUsers } = useSelectedUsers(locationId,CalendarStore);

  
  // fetch appointments from cache
  const {
    data: cachedAppointments,
    isLoading: appointmentsLoading,
    error: appointmentsError,
  } = useAppointmentsForDate(
    selectedDate,
    locationId!,
    weeklyWorkDay,
    { userIds },
    !continuousView,
    userIds.length > 0
  );
  // fetch continuous appointments from cache
  const { data: cachedContinuousAppointments } = useContinuousAppointmentsByDayAndLocation(
    locationId!,
    selectedDate.getDay(),
    !!locationId && continuousView
  );

  // fetch continuous appointments for selected user (when single user is selected)
  const selectedUserId = columnUserIds && columnUserIds.length === 1 ? columnUserIds[0] : undefined;
  const {
    data: cachedContinuousAppointmentsForUser,
    isLoading: isLoadingUserAppointments,
    error: userAppointmentsError,
  } = useContinuousAppointmentsForUser(
    selectedUserId,
    // Dauertermin data only — this query takes no date (it returns the user's
    // slots for EVERY weekday), so it has no business being loaded while the
    // Hauptkalender is showing a concrete day.
    continuousView && !!selectedUserId
  );

  const { isAppointmentOutsideWorkingHours, isTimeOutsideContinuousWorkingHours, isPastHorizon, horizonDate } = useCalendarValidations();


  // fetch patient unavailability for the selected patient
  const { data: patientUnavailabilityData } = usePatientUnavailabilityForCalendar(
    selectedPatientId,
    selectedDateStr,
    !!selectedPatientId
  );
  // fetch patient unavailability types by date for the selected patient and date
  const { data: patientUnavailabilityTypesData } = usePatientUnavailabilityTypeByDateForCalendar(
    selectedPatientId,
    selectedDateStr,
    !!selectedPatientId && !!selectedDateStr
  );
  // M U T A T I O N S        ***************************************************************************
  // Event creation mutation
  const { mutateAsync: saveAppointment } = useSaveAppointment();
  const { mutate: updateContinuousTimeSlot } = useUpdateContinuousTimeSlot();
  const { mutate: updateTimeSlot } = useUpdateTimeSlot();
  const { mutate: patchContinuousTimeSlotTimesAndUser } = usePatchContinuousTimeSlotTimesAndUser();
  const { mutate: patchTimeSlotTimesAndUser, mutateAsync: patchTimeSlotTimesAndUserAsync } =
    usePatchTimeSlotTimesAndUser();
  const { mutateAsync: scheduleUnscheduledTimeSlot } = useScheduleUnscheduledTimeSlot();
  const { mutateAsync: updateContinuousAppointmentMutation } = useUpdateContinuousAppointment();
  const { mutateAsync: createAndScheduleAppointment } = useCreateAndScheduleAppointment();
  const { createWorkHourException, updateWorkHourException } = useGuardedWorkHourExceptionMutations({
    promptOnOptionalBreak: false,
  });
  const { mutateAsync: deleteLunchBreakForDay } = useDeleteLunchBreakForDay();
  const { mutateAsync: deleteWeeklyWorkDay } = useDeleteWeeklyWorkDay();
  const { mutateAsync: deleteWorkHourException } = useDeleteWorkHourException();
  const createWorkingHoursForDay = useCreateWorkingHoursForDay();

  // this mutation handles the creation by the user of various time slot types (non-appointments)
  const createEventMutation = useMutation({
    mutationFn: async ({ event }: { event: CalendarEvent }) => {
      const userId: string = event.resourceId || "";
      const user = fetchedUsers.find((u: UserDto) => u.id === userId);
      let result: any;
      

      // setShowLoading(true);

      switch (event.type) {
        case "GoneFishing":
          const timeslot: TimeSlotDto = {
            user,
            type: "GoneFishing",
            start: moment(event.start!).toISOString(),
            end: moment(event.end!).toISOString(),
          };
          result = await createTimeslot(timeslot);
          break;
        case "LeaveOfAbsence":
          if (!user?.id) {
            throw new Error("User is required for LeaveOfAbsence");
          }
          const leaveOfAbsence: LeaveOfAbsenceDto = {
            user,
            type: "LeaveOfAbsence",
            loAType: event.loaType,
            start: moment(event.start!).toISOString(),
            end: moment(event.end!).toISOString(),
          };
          // Use userAbsence which handles overlaps and appointment cancellation
          result = await userAbsence({ userId: user.id, leaveOfAbsence });
          break;
        case "EventTimeSlot":
          const eventTimeSlot: EventTimeSlotDto = {
            user,
            type: "EventTimeSlot",
            title: event.title,
            start: moment(event.start!).toISOString(),
            end: moment(event.end!).toISOString(),
          };
          result = await createEventTimeSlot(eventTimeSlot);
          break;
        case "ContinuousEventTimeSlot":
          const continuousEventTimeSlot: ContinuousEventTimeSlotDto = {
            user,
            type: "ContinuousEventTimeSlot",
            title: event.title,
            start: moment(event.start!).toISOString(),
            end: moment(event.end!).toISOString(),
          };
          result = await createContinuousEventTimeSlot(continuousEventTimeSlot);
          break;
        case "SimpleWorkingHours":
          const simpleWorkingHours: SimpleWorkingHoursDto = {
            user,
            type: "SimpleWorkingHours",
            start: moment(event.start!).toISOString(),
            end: moment(event.end!).toISOString(),
          };
          result = await CalendarStore.api.postSimpleWorkingHours(simpleWorkingHours);
          break;
        default:
          break;
      }

      // console.log(result);
      return result;
    },
    onSuccess: (data, variables) => {
      const eventDate = moment(variables.event.start!).toDate();
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentsForDate.detail(eventDate),
      });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousAppointmentsByDay.detail(locationId!, eventDate.getDay()),
      });
      // console.debug(
        // "Create event successful",
        // data,
        // "invalidated based on date",
        // variables.event.start!
      // );
      notificationStore.showMessage("Event erfolgreich hinzugefügt", "success");
    },
    onError: (error) => {
      console.error("Create event failed", error);
      notificationStore.showMessage("Fehler beim Hinzufügen des Events", "error", error);
    },
    onSettled: () => {
      // setShowLoading(false);
    },
  });

  // R E F S
  // Refs to hold latest dynamic values for stable callbacks
  const fetchedUsersRef = useRef<UserDto[]>([]);
  const continuousViewRef = useRef<boolean>(continuousView);
  const selectedDateRef = useRef<Date>(selectedDate);
  const locationIdRef = useRef(locationId);
  const userIdsRef = useRef<string[]>(userIds);

  fetchedUsersRef.current = fetchedUsers;
  continuousViewRef.current = continuousView;
  selectedDateRef.current = selectedDate;
  locationIdRef.current = locationId;
  userIdsRef.current = userIds;

  // Refs for potentially non-stable store/service functions so callbacks can be [] deps
  const deleteTimeslotRef = useRef(deleteTimeslot);
  const deleteGroupTherapistRef = useRef(deleteGroupTherapist);
  const deleteContinuousTimeslotRef = useRef(deleteContinuousTimeslot);
  const setTargetPatientIdRef = useRef(setTargetPatientId);
  // Keep latest function references
  deleteTimeslotRef.current = deleteTimeslot;
  deleteGroupTherapistRef.current = deleteGroupTherapist;
  deleteContinuousTimeslotRef.current = deleteContinuousTimeslot;
  setTargetPatientIdRef.current = setTargetPatientId;

  // Latest values for the memoized `onCreateEvent` callback (manual break creation, MD-227).
  const derivedWorkingHoursRef = useRef(derivedWorkingHours);
  const createWorkHourExceptionRef = useRef(createWorkHourException);
  const updateWorkHourExceptionRef = useRef(updateWorkHourException);
  derivedWorkingHoursRef.current = derivedWorkingHours;
  createWorkHourExceptionRef.current = createWorkHourException;
  updateWorkHourExceptionRef.current = updateWorkHourException;

  // E F F E C T S   A N D   R E N D E R I N G   H A N D L E R S  *********************************************************
  /**
   * Handles updates to existing calendar events using the new adapter system.
   * Manages appointment locking during updates to prevent conflicts.
   * Processes both single events and related events for complex appointments.
   * Coordinates database updates for time, date, and user assignment changes.
   */
  const dispatchEventChange = async (eventChangeInfo: EventChangeArg) => {
    const event = eventChangeInfo.event as any as CalendarEvent;

    // Skip no-op changes (e.g. triggered by revert()) where start/end/resource are unchanged
    if (isNoOpEventChange(eventChangeInfo)) {
      console.debug("[CALENDAR CONTROLLER] onChangeEvent skipped — no actual change (likely a revert)");
      return;
    }

    // Horizon soft-block: moving a treatment or event time slot past the
    // configured horizon requires confirmation. Concrete calendar only —
    // the continuous (Dauertermin) view is horizon-projected by design.
    if (!continuousViewRef.current && event.start && isPastHorizon(event.start)) {
      const isTreatment = event.extendedProps.eventType === "appointment";
      const isEventSlot = event.extendedProps.timeSlotType === "EventTimeSlot";
      if (isTreatment || isEventSlot) {
        const proceed = await horizonConfirmStore.requestConfirm({
          date: event.start,
          horizonDate,
          kind: isEventSlot ? "event" : "appointment",
        });
        if (!proceed) {
          eventChangeInfo.revert();
          return;
        }
      }
    }

    // const userId = eventChangeInfo.event.getResources()?.[0]?.id || "";
    // const user = fetchedUsersRef.current.find((u: UserDto) => u.id === userId);
    // console.debug(
      // "[CALENDAR CONTROLLER] onChangeEvent ",
      // eventChangeInfo,
      // event,
      // event.extendedProps
    // );

    const eventType: EventType = event.extendedProps.eventType;
    const timeSlotType: string = event.extendedProps.timeSlotType;

    switch (eventType) {
      case "timeslot":
        // implement timeslot handling
        const tsDto = eventChangeToTimeSlotDto(eventChangeInfo);
        if (continuousViewRef.current) {
          updateContinuousTimeSlot({ id: tsDto.id!, timeslot: tsDto });
        } else {
          updateTimeSlot({ id: tsDto.id!, timeslot: tsDto });
        }
        break;
      case "appointment":
        if (continuousViewRef.current) {
          // Continuous appointments require aggregated start/end calculation
          const continuousDto = eventChangeToContinuousAppointmentUpdateData(eventChangeInfo);
          const caResponse = await updateContinuousAppointmentMutation({
            appointmentId: continuousDto.id!,
            appointment: continuousDto,
          });

          // No CA response stuff required when just updating an appointment
          // CalendarStore.openCAResponseDialog(caResponse);

          break;
        } else {
          // console.debug("Transforming from eventChangeInfo", eventChangeInfo);
          const appDto = eventChangeToAppointmentUpdateData(eventChangeInfo);
          // console.debug("Transformed AppointmentDto:", appDto);

          // A participant dropped onto an existing KGG group must carry that group's id,
          // otherwise the backend spawns a second group in the same slot.
          const targetUserId = eventChangeInfo.event.getResources()?.[0]?.id;
          appDto.timeSlots?.forEach((ts) => {
            if (!isGroupPatientType(ts.type)) return;
            const group = cachedAppointments?.appointments?.find((a) =>
              a.timeSlots?.some(
                (gs) =>
                  isGroupTherapistType(gs.type) &&
                  gs.user?.id === targetUserId &&
                  new Date(gs.start!).getTime() === new Date(ts.start!).getTime() &&
                  new Date(gs.end!).getTime() === new Date(ts.end!).getTime(),
              ),
            );
            ts.groupAppointmentId = group?.id;
          });

          // Moving ONE treatment of a multi-slot visit can leave the patient
          // waiting between their treatments — report the gap it opened.
          const survivor: any = await saveAppointment({ id: appDto.id!, appointment: appDto });
          showVisitGapWarningIfPresent(survivor);
          break;
        }

      case "availability":
        notificationStore.showMessage(
          "Patient availability updates not yet implemented",
          "warning"
        );
        break;
      case "working-hours":
        // implement working-hours handling
        if (timeSlotType === "LunchBreak" && !event.extendedProps.isContinuous) {
          // if it is exception-derived, then we want to update it, otherwise create it.
          const clientLunchBreak = eventToClientLunchBreak(eventChangeInfo.event);
          if (clientLunchBreak.isException) {
            // use a mutation to update the exception
            updateWorkHourException({
              exceptionId: clientLunchBreak.exceptionId!,
              exception: {
                userId: clientLunchBreak.userId,
                date: moment(clientLunchBreak.start).format("YYYY-MM-DD"),
                type: WorkHourExceptionDtoTypeEnum.Override,
                slots: [
                  {
                    slotId: clientLunchBreak.slotId, // Use original API slotId, not generated id
                    isBreak: true,
                    start: clientLunchBreak.start.toISOString(),
                    end: clientLunchBreak.end.toISOString(),
                  },
                ],
                initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
              },
            });
          } else {
            // use a mutation to create a new lunch break exception
            createWorkHourException({
              exception: {
                userId: clientLunchBreak.userId,
                date: moment(clientLunchBreak.start).format("YYYY-MM-DD"),
                type: WorkHourExceptionDtoTypeEnum.Override,
                slots: [
                  {
                    isBreak: true,
                    start: clientLunchBreak.start.toISOString(),
                    end: clientLunchBreak.end.toISOString(),
                  },
                ],
                initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
              },
            });
          }
        }
        else if (timeSlotType === "LunchBreak" && event.extendedProps.isContinuous) {
             const clientLunchBreak = eventToClientContinuousLunchBreak(eventChangeInfo.event);
              
              // For recurring weekly events, we need to store the time portion only
              // Create ISO strings with times on a reference date
              const weeklyWorkDay: WeeklyWorkDayDto = {
                id: clientLunchBreak?.weeklyWorkDay!.id!,
                workPlan: { id: clientLunchBreak?.weeklyWorkDay!.workPlan!.id! },
                dayOfWeek: clientLunchBreak.start.getDay(),
                startTime: clientLunchBreak.start.toISOString(),
                endTime: clientLunchBreak.end.toISOString(),
                employeeId: clientLunchBreak.userId,
              };
          
                await updateWeeklyWorkDayMutation.mutateAsync(weeklyWorkDay);
        }

         else {
          notificationStore.showMessage("Working hours updates not yet implemented", "warning");
        }
        break;
      default:
        console.log("Unknown event type:", eventType);
        notificationStore.showMessage("Unknown event type", "warning");
        legacyHandleEventChange(eventChangeInfo);
    } // end of switch on event type
  };

  /**
   * A move the server refuses is a normal outcome, not a crash: the scheduler
   * answers 409 ("Therapeut hat kein Heilmittel für diese Therapie", a slot
   * already taken, …), the MutationCache in queryClient.ts turns that into the
   * user's notification and the mutation's onError rolls its optimistic patch
   * back, which snaps the event home. Nothing is left for the caller to do —
   * but FullCalendar invokes `eventChange` fire-and-forget, so letting the
   * rejection out of here made it an `unhandledrejection` and, in dev, the
   * full-screen "Uncaught runtime errors" overlay on top of the notification
   * that had already explained the refusal.
   *
   * Since MD-232 the calendar deliberately lets prescription-backed moves reach
   * the backend rather than pre-gating them locally, so this path — a refusal
   * that arrives as a rejected mutation — is now walked in normal use.
   */
  const onChangeEvent = async (eventChangeInfo: EventChangeArg) => {
    try {
      await dispatchEventChange(eventChangeInfo);
    } catch (error) {
      console.error("[CALENDAR CONTROLLER] onChangeEvent failed", error);
    }
  };

  const legacyHandleEventChange = useCallback(
    async (eventChangeInfo: EventChangeArg) => {
      const event = eventChangeInfo.event as any as CalendarEvent;
      const userId = eventChangeInfo.event.getResources()?.[0]?.id || "";
      const user = fetchedUsersRef.current.find((u: UserDto) => u.id === userId);
      // console.debug(
        // "controller is processing event change for",
        // eventChangeInfo,
        // event,
        // event.extendedProps
      // );

      if (!event.extendedProps) {
        // Handle non-appointment events (timeslots, etc.)
        const updateData = adaptCalendarEventToUpdateData(event);
        const timeslot: TimeSlotDto = {
          id: event.timeSlotId!,
          user,
          start: updateData.start!.toISOString(),
          end: updateData.end!.toISOString(),
          type: event.eventType === "timeslot" ? "TimeSlot" : "EventTimeSlot",
        };

        if (continuousViewRef.current) {
          updateContinuousTimeSlot({ id: event.timeSlotId!, timeslot });
        } else {
          updateTimeSlot({ id: event.timeSlotId!, timeslot });
        }
      } else {
        // Handle appointment events using adapter system
        const updateData = adaptCalendarEventToUpdateData(event);

        if (continuousViewRef.current) {
          patchContinuousTimeSlotTimesAndUser({
            id: event.timeSlotId!,
            start: updateData.start!.toISOString(),
            end: updateData.end!.toISOString(),
            userId,
          });

          eventChangeInfo.relatedEvents?.forEach((relatedEvent: any) => {
            const relatedUpdateData = adaptCalendarEventToUpdateData(relatedEvent);
            patchContinuousTimeSlotTimesAndUser({
              id: relatedEvent.timeSlotId!,
              start: relatedUpdateData.start!.toISOString(),
              end: relatedUpdateData.end!.toISOString(),
              userId: relatedEvent.resourceId!,
            });
          });
        } else {
          // following is now replaced by the simpler saveAppointment call above
          /*
        patchTimeSlotTimesAndUser({
          id: event.timeSlotId!,
          start: updateData.start!.toISOString(),
          end: updateData.end!.toISOString(),
          userId,
        });

        relatedEvents?.forEach((relatedEvent: CalendarEvent) => {
          const relatedUpdateData = adaptCalendarEventToUpdateData(relatedEvent);
          patchTimeSlotTimesAndUser({
            id: relatedEvent.timeSlotId!,
            start: relatedUpdateData.start!.toISOString(),
            end: relatedUpdateData.end!.toISOString(),
            userId: relatedEvent.resourceId!,
          });
        });
        */
        }
      }
    },
    [
      updateContinuousTimeSlot,
      updateTimeSlot,
      patchContinuousTimeSlotTimesAndUser,
      patchTimeSlotTimesAndUser,
    ]
  );

  /**
   * Handles date changes in the calendar view.
   * Updates the selected date state and manages data refresh timing.
   * Currently includes throttling logic to prevent excessive API calls during rapid date changes.
   */
  const handleDateChange = useCallback((date: Date) => {
    // setFetchDelay(500);
    // setSelectedDateStr(moment(date).format(DATE_FORMAT));
    // setTimeout(() => setFetchDelay(0), 500);
  }, []);

  /**
   * Toggles between continuous and regular appointment view modes.
   * Manages fetching of different appointment types and clears shelf state.
   * Handles the transition between different calendar display modes with their specific data requirements.
   */
  const handleContinuousViewChange = useCallback(async (continuous: boolean) => {
    // const patientId = patient?.id?.toString();
    // if (patientId) {
    //   await fetchUnscheduledAppointments(patientId, continuous);
    // }
    // setContinuousView(continuous);
    // setShelfAppointments([]);
    // setShelfContinuousAppointments([]);
  }, []);

  /**
   * Toggles the cancelled appointments view filter.
   * Controls whether cancelled/resolved appointments are displayed in the calendar.
   */
  const handleCancelledViewChange = useCallback(
    (checked: boolean) => setCancelledView(checked),
    [setCancelledView]
  );

  /**
   * Creates new events in the calendar system.
   * Processes different event types (GoneFishing, LeaveOfAbsence, EventTimeSlot, etc.)
   * and delegates to appropriate creation services. Handles loading states and user feedback.
   */
  const createEventMutateAsyncRef = useRef(createEventMutation.mutateAsync);
  createEventMutateAsyncRef.current = createEventMutation.mutateAsync;
  const onCreateEvent = useCallback(async (event: CalendarEvent) => {
    try {
      // Handle WorkingHours type - create working hours exception
      if (event.type === 'WorkingHours') {
        const userId = event.resourceId;
        const date = moment(event.start).format("YYYY-MM-DD");
        const startTime = moment(event.start).format("HH:mm");
        const endTime = moment(event.end).format("HH:mm");

        if (!userId || !date) {
          throw new Error("Cannot create working hours: missing userId or date");
        }

        await createWorkingHoursForDay.mutateAsync({
          userId,
          date,
          startTime,
          endTime,
        });
        return;
      }

      // Handle LunchBreak type (MD-227) — the user manually drew a break on
      // the main calendar. Send ONLY the break slot as an Override; the
      // backend composes the full day (planned work + this break), enforces
      // the §4 minimum and validates the position. Create vs. update depends
      // on whether the day already has an exception.
      if (event.type === 'LunchBreak') {
        const userId = event.resourceId;
        if (!userId || !event.start || !event.end) {
          throw new Error("Cannot create break: missing user or time range");
        }
        const date = moment(event.start).format("YYYY-MM-DD");
        const existingException = derivedWorkingHoursRef.current?.find(
          (wh) => wh.userId === userId && wh.date === date && wh.source === 'exception'
        );
        const breakSlot = {
          isBreak: true,
          start: moment(event.start).toISOString(),
          end: moment(event.end).toISOString(),
        };
        if (existingException?.exceptionId) {
          await updateWorkHourExceptionRef.current({
            exceptionId: existingException.exceptionId,
            exception: {
              userId,
              date,
              type: WorkHourExceptionDtoTypeEnum.Override,
              slots: [breakSlot],
              initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
            },
          });
        } else {
          await createWorkHourExceptionRef.current({
            exception: {
              userId,
              date,
              type: WorkHourExceptionDtoTypeEnum.Override,
              slots: [breakSlot],
              initiator: WorkHourExceptionDtoInitiatorEnum.Employer,
            },
          });
        }
        return;
      }

      // Other event types handled here...
      // setShowLoading(true);

      // Horizon soft-block: creating an event time slot past the horizon needs
      // confirmation (continuous events are horizon-projected and exempt).
      if (event.type === "EventTimeSlot" && event.start && isPastHorizon(event.start)) {
        const proceed = await horizonConfirmStore.requestConfirm({
          date: event.start,
          horizonDate,
          kind: "event",
        });
        if (!proceed) return;
      }

      await createEventMutateAsyncRef.current({ event });
    } catch (error) {
      console.error("Event creation error", error);
    } finally {
      // setShowLoading(false);
    }
  }, [createWorkingHoursForDay, isPastHorizon, horizonDate]);

  /**
   * Handler for receiving an event via drag-and-drop from outside the calendar.
   * Processes external events (from shelf) being dropped onto calendar slots.
   * Updates appointment times and user assignments, manages shelf state cleanup.
   * Handles both regular and continuous appointment modes with appropriate save operations.
   */
  const onEventReceive = useCallback(
    async (eventReceive: EventReceiveArg) => {
      try {
        // setShowLoading(true);
        const event = eventReceive.event;
        // console.log("Event", event.toPlainObject());
        const userId = eventReceive.event.getResources()[0]?.id;

        // Horizon soft-block: scheduling a treatment past the configured horizon
        // requires explicit confirmation. Cancel discards the drop.
        if (event.start && isPastHorizon(event.start)) {
          const proceed = await horizonConfirmStore.requestConfirm({
            date: event.start,
            horizonDate,
            kind: "appointment",
          });
          if (!proceed) {
            event.remove();
            return;
          }
        }

        // Dynamic Template Provider drop (MD-148): create AND schedule a brand
        // new appointment from the prescribed therapies in one request, then
        // remove the provisional template event (its temp id never matches the
        // real appointment, so FullCalendar can't reconcile it on refetch).
        if (event.extendedProps.isTemplate) {
          const template = event.extendedProps.template;
          const start = event.start;
          if (!template?.therapyRxIds?.length || !start || !userId) {
            notificationStore.showMessage(
              "Vorlage konnte nicht geplant werden (fehlende Daten).",
              "warning"
            );
            event.remove();
            return;
          }

          // Address is mandatory and the visit fee must be a deliberate choice
          // per therapy. When either could not be inferred from the Rx's prior
          // appointments, defer to the missing-data dialog instead of creating.
          if (template.needsInput || !template.addressId) {
            CalendarStore.openTemplateScheduleDialog({
              rxId: template.rxId,
              patientId: Number(template.patientId),
              therapyRxIds: template.therapyRxIds,
              start: start.toISOString(),
              userId,
              inferredAddressId: template.addressId,
              inferredVisitFees: template.visitFees ?? {},
            });
            // The provisional event is only a placeholder; the dialog drives the
            // real creation, so drop it now.
            event.remove();
            return;
          }

          try {
            await createAndScheduleAppointment({
              dto: {
                therapyRxIds: template.therapyRxIds,
                start: start.toISOString(),
                userId,
                addressId: template.addressId,
                visitFees: template.visitFees,
              },
              patientId: Number(template.patientId),
            });
          } finally {
            // The provisional template event is replaced by the real, refetched
            // appointment — always drop it so no orphan placeholder remains.
            event.remove();
          }
          return;
        }

        // Per-timeslot shelf drag (single-slot scheduleUnscheduled): the payload
        // carries exactly one slot and the `isSingleTimeSlot` discriminator. Route
        // it to the slot-level verb which splits-or-cleans-up the source
        // appointment, resolves any cancellation, and merges into the same-day
        // appointment — instead of the whole-appointment reschedule.
        if (event.extendedProps.isSingleTimeSlot) {
          const slotId = event.extendedProps.timeSlots?.[0]?.id;
          if (!slotId || !event.start || !event.end || !userId) {
            notificationStore.showMessage(
              "Termin konnte nicht geplant werden (fehlende Daten).",
              "warning"
            );
            event.remove();
            return;
          }
          // Slot pins are stored under the SLOT id (event.id carries the host
          // appointment id for single-slot payloads). Captured up front so the pin
          // can be dropped together with the optimistic cache patch and restored
          // if the reschedule fails.
          const pinnedSlotItem = (CalendarStore.pinBoardItems as any[]).find(
            (item: any) => item?.id === slotId && item?.isSlotPin
          );
          try {
            if (event.extendedProps.isScheduledSlotMove) {
              // Slot pin from "Umziehen → Nur diese": the slot is STILL scheduled in
              // the backend (the move was client-side only), so use the atomic
              // slot-level reschedule — it splits the slot off its appointment,
              // applies the new window/user and merges same-day, in one transaction.
              await patchTimeSlotTimesAndUserAsync({
                id: slotId,
                start: event.start.toISOString(),
                end: event.end.toISOString(),
                userId,
                // Un-pin in the SAME render cycle as the optimistic patch: the pinned
                // slot is filtered OUT of the events array, so un-pinning only after
                // the server responds leaves the placed slot invisible for the whole
                // roundtrip (vanish-then-pop-in flash).
                onOptimisticApplied: pinnedSlotItem
                  ? () => handleRemoveFromPinBoard(slotId)
                  : undefined,
              });
            } else {
              const result: any = await scheduleUnscheduledTimeSlot({
                id: slotId,
                start: event.start.toISOString(),
                end: event.end.toISOString(),
                userId,
                // A slot lifted off the Ablage has no query-cache entry to build the
                // optimistic insert from (the pinboard is MobX state), so hand the
                // pin over as the insert base. "Offene Termine" drags leave this
                // undefined and the mutation finds the hydrated source in the shelf
                // query itself.
                optimisticAppointment: pinnedSlotItem,
                // Un-pin in the SAME render cycle as the optimistic insert, so the
                // pinboard filter releases the slot exactly as it appears on the grid.
                onOptimisticApplied: pinnedSlotItem
                  ? () => handleRemoveFromPinBoard(slotId)
                  : undefined,
              });
              showVisitGapWarningIfPresent(result);
            }
            handleRemoveFromPinBoard(slotId);
          } catch (error) {
            // The mutation rolled its cache patch back — restore the optimistically
            // removed pin so the staged "Umziehen" move is not lost.
            if (pinnedSlotItem) handleAddToPinBoard(pinnedSlotItem);
            throw error;
          } finally {
            event.remove();
          }
          return;
        }

        // const user = fetchedUsers.find((u: UserDto) => u.id === userId);
        const oldAppointment: AppointmentDto = {
          id: event.id!,
          timeSlots: event.extendedProps.timeSlots,
          frequency: event.extendedProps.frequency,
        };
        const newAppointment = updateReceivedAppointment(
          oldAppointment,
          userId,
          event.start!,
          event.end!
        );
        // console.log("Appointment to save", newAppointment);
        if (newAppointment) {
          if (continuousViewRef.current) {
            // Open dialog immediately to show loading state
            CalendarStore.openCAResponseDialog();

            // Handle continuous appointment update - may return CAResponse
            const caResponse = await updateContinuousAppointmentMutation({
              appointmentId: event.id!,
              appointment: newAppointment as any,
            });
            // console.log("save Continuous Appointment result", caResponse);

            // Set CAResponse data when available (dialog already open)
            if (caResponse && caResponse.backPlanResults?.length > 0) {
              CalendarStore.setCAResponseData(caResponse);
            } else {
              // Close dialog if no response data
              CalendarStore.closeCAResponseDialog();
            }
          } else {
            // Handle regular appointment update.
            //
            // The `newAppointment` payload is built lean by updateReceivedAppointment
            // (no therapyRx.rx.patient), so an optimistic cache INSERT from it would
            // briefly render a patient-less event until the server DTO lands. Find the
            // fully-hydrated source appointment (with patient + relations) from the
            // shelf — the pinboard ("Ablage") or the unscheduled "Offene Termine"
            // caches — and pass it so the optimistic insert is render-complete.
            const pinnedItem = (CalendarStore.pinBoardItems as AppointmentDto[]).find(
              (item) => item?.id === event.id && !!(item as AppointmentDto)?.timeSlots
            );
            const optimisticSource =
              pinnedItem ??
              queryClient
                .getQueriesData<AppointmentDto[]>({
                  queryKey: calendarKeys.unscheduledPatientAppointments.all(),
                })
                .flatMap(([, list]) => list ?? [])
                .find((a) => a?.id === event.id);

            try {
              // Handle regular appointment update
              const result: any = await saveAppointment({
                id: event.id!,
                appointment: newAppointment,
                optimisticAppointment: optimisticSource,
                // Un-pin in the SAME render cycle as the optimistic cache patch:
                // pinned appointments are filtered OUT of the events array, so
                // removing the pin only after the server responds leaves the placed
                // event invisible for the whole roundtrip — the "vanishes, then
                // pops in later" flash when completing an Umziehen move.
                onOptimisticApplied: pinnedItem
                  ? () => handleRemoveFromPinBoard(event.id!)
                  : undefined,
              });
              // console.log("save Appointment result", result);

              // Show warning dialog if appointment.mergeAppointmentGaps is provided and not zero
              showMergeGapWarningIfPresent(result);
            } catch (error) {
              // The mutation rolled its cache patch back — restore the optimistically
              // removed pin so the staged "Umziehen" move is not lost.
              if (pinnedItem) handleAddToPinBoard(pinnedItem);
              throw error;
            }
          }

          // if (result) {
          //   setShelfAppointments(shelfAppointments.filter((app) => app.id !== event.id));
          //   setShelfContinuousAppointments(
          //     shelfContinuousAppointments.filter((app) => app.id !== event.id)
          //   );
          //   setUnscheduleds(unscheduleds.filter((app) => app.id !== event.id));
          //   notificationStore.showMessage("Termin erfolgreich umgezogen", "success");
          // }

          handleRemoveFromPinBoard(event.id!);
        }
      } catch (error) {
        console.error("Error in onEventReceive:", error);
      } finally {
        // setShowLoading(false);
      }
    },
    [saveAppointment, continuousViewRef, createAndScheduleAppointment, showMergeGapWarningIfPresent, isPastHorizon, horizonDate]
  );


  /**
   * Handles scheduling of selected shelf appointments at specific calendar slots.
   * Processes appointments dropped from the shelf with user-specified timing.
   * Updates appointment details and removes from shelf upon successful placement.
   * Coordinates with both regular and continuous appointment save operations.
   */
  const onSelectedShelfEventReceive = useCallback(
    async (appointment: AppointmentDto) => {
      // try {
      //   // setShowLoading(true);
      //   const result = continuousView
      //     ? await updateContinuousWithResponse(appointment.id!, appointment)
      //     : await saveAppointment(appointment.id!, appointment);
      //   console.log(result);
      //   if (result) {
      //     setShelfAppointments(shelfAppointments.filter((app) => app.id !== appointment.id));
      //     setShelfContinuousAppointments(
      //       shelfContinuousAppointments.filter((app) => app.id !== appointment.id)
      //     );
      //     setUnscheduleds(unscheduleds.filter((app) => app.id !== appointment.id));
      //     notificationStore.showMessage("Termin erfolgreich hinzugefügt", "success");
      //   }
      // } catch (error) {
      //   console.error("Error in onSelectedShelfEventReceive:", error);
      // } finally {
      //   // setShowLoading(false);
      // }
    },
    [
      /* stable */
    ]
  );

  /**
   * Removes a lunch break.
   * - Regular view: creates an exception to exclude the lunch break for that specific day only.
   * - Continuous (Dauertermin) view: the break is a weekly work day (break slot) of the
   *   user's work plan, so it is deleted via user-profile/deleteWeeklyWorkDay — same as
   *   the user work plan admin page.
   */
  const onRemoveLunchBreak = useCallback(async (event: CalendarEvent) => {
    try {
      const userId = event.resourceId || event.extendedProps?.user?.id;

      if (event.extendedProps?.isContinuous) {
        const weeklyWorkDay: WeeklyWorkDayDto | undefined =
          event.extendedProps?.weeklyWorkDay ?? (event as any).weeklyWorkDay;

        if (!userId || weeklyWorkDay?.id == null) {
          throw new Error("Cannot delete lunch break: missing userId or weekly work day");
        }

        await deleteWeeklyWorkDay({
          weeklyWorkDayId: weeklyWorkDay.id,
          userId,
          workPlanId: weeklyWorkDay.workPlan?.id,
        });
        return;
      }

      const date = moment(event.start).format("YYYY-MM-DD");

      if (!userId || !date) {
        throw new Error("Cannot delete lunch break: missing userId or date");
      }

      // Per-break delete (MD-238 P3): only the selected break is removed.
      await deleteLunchBreakForDay({ userId, date, slotId: event.extendedProps?.slotId });
      // console.log("Lunch break excluded for day", { userId, date });
    } catch (error) {
      console.error("Error deleting lunch break:", error);
    }
  }, [deleteLunchBreakForDay, deleteWeeklyWorkDay]);

  /**
   * Removes regular timeslot events from the calendar and database.
   * Handles deletion of various timeslot types (GoneFishing, EventTimeSlots, etc.).
   * Manages loading states during deletion operations.
   * For lunch breaks, delegates to onRemoveLunchBreak if applicable.
   */
  const onRemoveEvent = useCallback(async (timeslotId: string, event?: CalendarEvent) => {
    try {
      // console.log("[onRemoveEvent] Called with:", { timeslotId, event, timeSlotType: event?.extendedProps?.timeSlotType });
      
      // Check if this is a lunch break
      if (event?.extendedProps?.timeSlotType === 'LunchBreak') {
        // console.log("[onRemoveEvent] Detected lunch break, delegating to onRemoveLunchBreak");
        // Delegate to lunch break handler
        return await onRemoveLunchBreak(event);
      }
      
      // console.log("[onRemoveEvent] Not a lunch break, calling deleteTimeslot");
      const result = await deleteTimeslotRef.current(timeslotId);
      // console.log(result);

      // This ensures "Offene Termine" updates when sick/vacation etc is removed
      queryClient.invalidateQueries({
        queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
      });
      queryClient.invalidateQueries({
        queryKey: [appointmentKeys.unscheduledAppointments.scope],
      });
    } catch (error) {
      console.error("Error in onRemoveEvent:", error);
    }
  }, [onRemoveLunchBreak, queryClient]);

  const onRemoveGroupTherapist = useCallback(async (timeslotId: string) => {
    try {
      // console.log("Removing group therapist with timeslotId:", timeslotId);
      const result = await deleteGroupTherapistRef.current({id: timeslotId});
      // console.log(result);
    } catch (error) {
      console.error("Error in onRemoveGroupTherapist:", error);
    }
  },[]);

  /**
   * Removes continuous timeslot events from the calendar and database.
   * Specialized deletion handler for continuous appointment timeslots.
   *
   * After deletion, we invalidate the continuous appointments query caches.
   * Otherwise the deleted timeslot remains in the React Query cache and
   * will be re-rendered the next time the calendar's event list is
   * recomputed from cache (e.g. after a lunch break move triggers a
   * working-hours invalidation and re-render).
   */
  const onRemoveContinuousEvent = useCallback(async (timeslotId: string) => {
    const result = await deleteContinuousTimeslotRef.current(timeslotId);
    // console.log(result);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousAppointmentsByDay.all(),
      }),
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousAppointmentsForUser.all(),
      }),
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousAppointmentsForPatient.all(),
      }),
      queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
      }),
    ]);
  }, [queryClient]);

  /**
   * Moves appointments from calendar to the shelf for later scheduling.
   * Retrieves full appointment data and adds to appropriate shelf collection.
   * Provides user feedback and manages shelf state for both regular and continuous appointments.
   */
 const onMoveEventDate = useCallback(async (event: CalendarEvent) => {
  // console.log("Event moved to onMoveEventDate", event);
    // if (continuousView) {
    //   const continuousAppDto: ContinuousAppointmentDto = await getContinuousAppointment(
    //     event.appointmentId!
    //   );
    //   console.log("Event moved to shelf box:", continuousAppDto);
    //   continuousAppDto.timeSlots?.sort(
    //     (a: TimeSlotDto, b: TimeSlotDto) =>
    //       new Date(a.start).getTime() - new Date(b.start).getTime()
    //   );
    //   notificationStore.showMessage("Dauertermin ins Regal verschoben", "info");
    //   setShelfContinuousAppointments([...shelfContinuousAppointments, continuousAppDto]);
    // } else {

    // use lookup instead:
     const isGroupPatient = event.extendedProps?.timeSlotType==="GroupTimeSlotPatient" || event.extendedProps?.timeSlotType==="ContinuousGroupTimeSlotPatient" || (event.extendedProps as any)?.type==="GroupTimeSlotPatient" || (event.extendedProps as any)?.type==="ContinuousGroupTimeSlotPatient";
     const isContinuousEvent = event.extendedProps?.isContinuous;
     if(isGroupPatient){
      // Update raw cache: remove participant from therapist's participants array
      // Update the appropriate cache based on whether it's continuous or regular
      if (isContinuousEvent) {
        // Update continuous appointments caches
        const updateContinuousCache = (oldData: any) => {
          if (!oldData?.appointments) return oldData;
          return {
            ...oldData,
            appointments: oldData.appointments
              .map((appointment: any) => {
                if (appointment.id === event.extendedProps.groupAppointmentId!) {
                  const participants = appointment.participants || [];
                  const filteredParticipants = participants.filter((p: any) => p.timeSlots?.some((ts:any)=>ts.id !== event.id));

                  // Keep the therapist appointment even with 0 participants (only explicit delete should remove it)
                  return { ...appointment, participants: filteredParticipants };
                }
                return appointment;
              }),
          };
        };
        // Update all continuous appointment caches
        queryClient.setQueriesData(
          { queryKey: calendarKeys.continuousAppointmentsByDay.all() },
          updateContinuousCache
        );
        queryClient.setQueriesData(
          { queryKey: calendarKeys.continuousAppointmentsForUser.all() },
          updateContinuousCache
        );
      } else {
        queryClient.setQueryData(
          calendarKeys.appointmentsForDate.detail(selectedDateRef.current, userIdsRef.current),
          (oldData: any) => {
            if (!oldData?.appointments) return oldData;
          
            return {
              ...oldData,
              appointments: oldData.appointments
                .map((appointment: AppointmentDto) => {
                  // Find the therapist appointment that contains this patient
                  if (appointment.id === event.extendedProps.groupAppointmentId!) {
                    const participants = appointment.participants || [];
                    const filteredParticipants = participants.filter((p: any) => p.timeSlots?.some((ts:any)=>ts.id !== event.id));

                    // When the last participant is moved out, drop the now-empty
                    // group-therapist umbrella so the empty group disappears from
                    // the calendar
                    if (filteredParticipants.length === 0) {
                      return null;
                    }

                    return {
                      ...appointment,
                      participants: filteredParticipants,
                    };
                  }
                  return appointment;
                })
                .filter(Boolean),
            };
          }
        );
      }


    }

    const appDto: AppointmentDto = lookupByAppointmentId(event.extendedProps.appointmentId!)!;
    if (!appDto) {
      return;
    }

    // Pre-select the pin on the shelf so a keyboard user can lift with "v" and
    // immediately place it at the cell cursor with "b"/"v" — no mouse needed.
    const shelfAddressPayload = (() => {
      const address = (appDto as any)?.address;
      const coordinates = address?.coordinates;
      return coordinates?.latitude != null && coordinates?.longitude != null
        ? { id: address.id, latitude: coordinates.latitude, longitude: coordinates.longitude }
        : null;
    })();

    // "Nur diese" (single-slot move): pin ONLY the clicked slot, purely client-side.
    // The pin is appointment-shaped (so the shelf can render/drag it) but carries the
    // SLOT id + discriminators: the calendar hides just that slot and the drop routes
    // to the atomic slot-level reschedule verb. Nothing is persisted until the drop,
    // so X/Escape fully restores the original state.
    if ((event.extendedProps as any)?.moveSingleSlot) {
      const slotId =
        event.id ??
        (event.extendedProps as any)?.timeSlotId ??
        (event.extendedProps as any)?.timeSlot?.id;
      const slot = appDto.timeSlots?.find((ts: TimeSlotDto) => ts.id === slotId);
      if (!slot) {
        console.error("moveSingleSlot: slot not found on host appointment", { slotId });
        return;
      }
      notificationStore.showMessage("Behandlung ins Regal verschoben", "info");
      handleAddToPinBoard({
        ...appDto,
        id: slot.id,
        timeSlots: [slot],
        isSlotPin: true,
        pinnedAppointmentId: appDto.id,
      });
      CalendarStore.setSelectedShelfAppointment(slot.id!, shelfAddressPayload);
      return;
    }

    appDto.timeSlots?.sort(
      (a: TimeSlotDto, b: TimeSlotDto) => new Date(a.start).getTime() - new Date(b.start).getTime()
    );
    // console.log("Event moved to shelf box:", appDto);
    notificationStore.showMessage("Termin ins Regal verschoben", "info");
    // setShelfAppointments([...shelfAppointments, appDto]);
    handleAddToPinBoard(appDto);
    CalendarStore.setSelectedShelfAppointment(appDto.id!, shelfAddressPayload);
    // }
  }, []);

  /**
   * Adds appointments from shelf to specific calendar time slots.
   * Processes the last item from shelf and schedules it at the specified time/resource.
   * Handles both continuous and regular appointments with appropriate save operations.
   * Manages shelf state cleanup and provides user feedback on success/failure.
   */
  const onAddShelfEvent = useCallback(async (event: CalendarEvent) => {
    // console.log("Event moved from shelf box:", event);
    // const lastShelfEvent = continuousView
    //   ? shelfContinuousAppointments[shelfContinuousAppointments.length - 1]
    //   : shelfAppointments[shelfAppointments.length - 1];
    // const userId = event.resourceId || "";
    // const user = fetchedUsers.find((u: UserDto) => u.id === userId);
    // let current = new Date(event.start);
    // if (lastShelfEvent) {
    //   try {
    //     const result = continuousView
    //       ? await updateContinuousWithResponse(
    //           lastShelfEvent.id!,
    //           lastShelfEvent as ContinuousAppointmentDto
    //         )
    //       : await saveAppointment(lastShelfEvent.id!, lastShelfEvent as AppointmentDto);
    //     console.log(result);
    //     setShelfAppointments(shelfAppointments.filter((app) => app.id !== lastShelfEvent.id));
    //     setShelfContinuousAppointments(
    //       shelfContinuousAppointments.filter((app) => app.id !== lastShelfEvent.id)
    //     );
    //     notificationStore.showMessage("Termin erfolgreich hinzugefügt", "success");
    //   } catch (error) {
    //     console.log(error);
    //     notificationStore.showMessage("Fehler beim Hinzufügen des Events", "error", error);
    //   } finally {
    //     await renderCalendar();
    //   }
    // }
  }, []);

  /**
   * Handles patient tab changes in the patient drawer.
   * Fetches patient-specific data when switching between patient tabs.
   * Updates patient context and related appointment information.
   */
  const onPatientTabChange = useCallback(async (newPatientId: string) => {
    // fetchPatient(newPatientId);
    // // TODO: fetchPatientAppointments(newPatientId); - function needs to be implemented
    // fetchUnscheduledAppointments(newPatientId, continuousView);
  }, []);

  /**
   * Updates the target patient context for calendar operations.
   * Sets the active patient for appointment operations and fetches related data.
   * Used when changing the primary patient context in the interface.
   */
  const onTargetPatientChange = useCallback(async (newTarget: PatientDto) => {
    setTargetPatientIdRef.current(newTarget.id);
  }, []);

  /**
   * Handles selection of specific appointments from the unscheduled list.
   * Updates the selected shelf appointment state for keyboard-based scheduling operations.
   */
  const onSelectShelfAppointment = useCallback((appId: string) => {
    // const selectedAppointment = unscheduleds.find((app) => app.id === appId);
    // if (selectedAppointment) {
    //   setSelectedShelfAppointment(selectedAppointment);
    // }
  }, []);

  /**
   * Refreshes unscheduled appointments data for the current patient.
   * Triggered when appointment data needs to be updated due to external changes.
   * Manages loading states during data refresh operations.
   */
  const onDataRefreshFlag = useCallback(async () => {
    // try {
    //   if (!patient?.id) {
    //     console.error("Patient not found");
    //     return;
    //   }
    //   // setShowLoading(true);
    //   await fetchUnscheduledAppointments(patient.id.toString(), continuousView);
    // } catch (error) {
    //   console.error("Error fetching unscheduled appointments:", error);
    // } finally {
    //   // setShowLoading(false);
    // }
  }, []);

  /**
   * Forces a complete calendar data refresh by invalidating React Query caches.
   * Triggers re-fetch of all calendar-related data.
   * Used when calendar data needs to be synchronized with backend changes.
   */
  const invalidateCalendarQueries = useCallback(async () => {
    // Shared with the MobiliCalendar toolbar — see calendar.keys.ts.
    await invalidateAllCalendarQueries(queryClient);
  }, [queryClient]);

  /**
   * Generic handler for triggering calendar data refresh.
   * Wrapper around renderCalendar for external components to request data updates.
   * Used when other components need to signal that calendar data may be outdated.
   */
  const onOutdated = useCallback(() => {
    console.info("[Calendar Controller Handler] onOutdated called - refreshing data");
    void invalidateCalendarQueries();
  }, [invalidateCalendarQueries]);

  const mergeGroupPatientIntoTherapistAppointments = (
    appointments: AppointmentDto[],
  ): AppointmentDto[] => {
    const therapistMap = new Map<string, AppointmentDto>();

    // 1. Collect therapist appointments and initialize participants
    for (const appointment of appointments) {
      const therapistSlot = appointment.timeSlots?.find(
        (ts) => ts.type === "GroupTimeSlotTherapist",
      );
      if (therapistSlot) {
        // Participants are expected to arrive from the backend as proper
        // AppointmentDto shapes (with `id` = appointment id, `timeSlots` array,
        // patient data, etc.). No frontend normalization needed.
        therapistMap.set(appointment.id!, {
          ...appointment,
          address: appointment.address || undefined,
          participants: appointment.participants || [],
        });
      }
    }

    // 2. Go through all appointments and enrich matching participants
    // with full appointment data (address, hasMultipleTimeSlots, etc.)
    for (const appointment of appointments) {
      if (!appointment.timeSlots) continue;

      appointment.timeSlots.forEach((ts) => {
        if (ts.type !== "GroupTimeSlotPatient" || !ts.groupAppointmentId) return;

        const therapistApp = therapistMap.get(ts.groupAppointmentId);
        if (!therapistApp || !therapistApp.participants || therapistApp.participants.length === 0)
          return;

        // Match participant by its timeslot ID
        const existingIndex = therapistApp.participants.findIndex(
          (participant) => participant.timeSlots?.[0]?.id === ts.id,
        );

        if (existingIndex >= 0) {
          // Enrich the participant with data from the standalone appointment
          therapistApp.participants[existingIndex] = {
            ...therapistApp.participants[existingIndex],
            id: appointment.id,
            address: appointment.address,
            notes: appointment.notes,
            hasNotes: (appointment as any).hasNotes,
            timeSlots: [{ ...ts }],
          } as any;
        }
      });
    }


    // 4. Filter out original appointments that have ALL timeslots moved
    const remainingAppointments = appointments.filter(
      (app) =>
        !app.timeSlots?.every(
          (ts) =>
            ts.type === "GroupTimeSlotPatient" &&
            ts.groupAppointmentId &&
            therapistMap.has(ts.groupAppointmentId),
        ),
    );

    // 5. Return merged result
    // Drop group-therapist umbrellas that have no participants left (e.g. the
    // last participant was moved out) so the empty group does not linger on the
    // calendar even after a backend refetch — unless the umbrella itself is a
    // cancellation, which must stay visible in the "Stornierungen" view.
    const mergedTherapistAppointments = Array.from(therapistMap.values()).filter(
      (app) => (app.participants?.length || 0) > 0 || isCancelledAppointment(app),
    );
    return [
      ...mergedTherapistAppointments,
      ...remainingAppointments.filter((app) => !therapistMap.has(app.id!)),
    ];
  };

//  const mergeGroupPatientIntoTherapistAppointments = (appointments: AppointmentDto[]): any[] => {
//   const therapistMap = new Map<string, any>();

//   // 1. Collect therapist appointments and initialize participants
//   for (const appointment of appointments) {
//     const therapistSlot = appointment.timeSlots?.find(
//       (ts: any) => ts.type === "GroupTimeSlotTherapist"
//     );

//     if (therapistSlot) {
//       therapistMap.set(appointment.id!, {
//         ...appointment,
//         participants: [], // will fill later
//       });
//     }
//   }

//   // 2. Go through all appointments and attach matching patients
//   for (const appointment of appointments) {
//     if (!appointment.timeSlots) continue;

//     // Collect slots to move
//     const slotsToMove: any[] = [];

//     appointment.timeSlots.forEach((ts: any) => {
//       if (
//         ts.type === "GroupTimeSlotPatient" &&
//         ts.groupAppointmentId &&
//         therapistMap.has(ts.groupAppointmentId)
//       ) {
//         slotsToMove.push(ts);
//       }
//     });

//     // Move each slot to therapist participants
//     for (const slot of slotsToMove) {
//       const therapistApp = therapistMap.get(slot.groupAppointmentId);
//       if (therapistApp) {
//         // Create a clone of the appointment for participants
//         therapistApp.participants.push({
//           ...appointment,
//           timeSlots: [ { ...slot } ], // clone the slot so original can be modified safely
//         });

//         // Remove this slot from the original appointment
//         // appointment.timeSlots = appointment.timeSlots.filter(
//         //   (ts: any) => ts !== slot
//         // );
//       }
//     }
//   }

//   // 3. Filter out original appointments that have ALL timeSlots as GroupTimeSlotPatient linked to therapists
//   // Keep appointments that have at least one timeSlot that is NOT a grouped patient
//   const remainingAppointments = appointments.filter(
//     app => !app.timeSlots?.every((ts: any) => ts.type === "GroupTimeSlotPatient" && ts.groupAppointmentId && therapistMap.has(ts.groupAppointmentId))
//   );

//   // 4. Combine therapists with participants + remaining appointments
//   return [...therapistMap.values(), ...remainingAppointments.filter(app => !therapistMap.has(app.id!  ))];
// };

  // Prepare filtered appointments based on cancelled view
  const filteredAppointments = useMemo(() => {
    if (!cachedAppointments?.appointments) return [];
    // Build a fast lookup set of pinned appointment ids (only regular appointments with timeSlots)
    const pinnedIds = new Set(
      (pinBoardItems || [])
        .filter((item: any) => item && (item as any).timeSlots && !(item as any).isSlotPin) // distinguish from continuous time slots objects
        .map((item: any) => item.id)
    );
    // Slot-level pins ("Umziehen → Nur diese"): hide ONLY that slot, keep siblings visible.
    const pinnedSlotIds = new Set(
      (pinBoardItems || [])
        .filter((item: any) => item && (item as any).isSlotPin)
        .map((item: any) => item.id)
    );
    // First apply cancelled view logic, then remove pinned items.
    // The backend mixes active + cancelled appointments into the same response,
    // distinguishing cancellations by `originalDate` + `resolved === false`
    // (see isCancelledAppointment). We never used to look at `end === null`
    // for this — that was a stale marker that silently broke the toggle.
    const base = isCancelledView
      ? cachedAppointments.appointments
      : cachedAppointments.appointments.filter(
          (appointment: any) => !isCancelledAppointment(appointment),
        );
    const merged = mergeGroupPatientIntoTherapistAppointments(base);
    const filtered = merged
      .filter((appointment: any) => !pinnedIds.has(appointment.id))
      .map((appointment: any) => {
        if (
          pinnedSlotIds.size === 0 ||
          !appointment.timeSlots?.some((ts: any) => pinnedSlotIds.has(ts.id))
        ) {
          return appointment;
        }
        const remainingSlots = appointment.timeSlots.filter(
          (ts: any) => !pinnedSlotIds.has(ts.id)
        );
        return remainingSlots.length > 0 ? { ...appointment, timeSlots: remainingSlots } : null;
      })
      .filter(Boolean);

    return filtered;
  }, [cachedAppointments, isCancelledView, pinBoardItems]);



  // P R O C E S S   D A T A   A N D   M E M O I Z A T I O N  *********************************************************
  // data converters and processors to get the final data types ready for the calendar

  // deconstruct appointments cache
  const { leaveOfAbsences, eventTimeSlots, travelTimes } = cachedAppointments || {
    appointments: [],
    goneFishings: [],
    leaveOfAbsences: [],
    eventTimeSlots: [],
    lunchBreaks: [],
    travelTimes: [],
    simpleWorkingHours: [],
  };

  // deconstruct continuous appointments cache

  const {
    appointments: rawContinuousAppointments = [],
    eventTimeSlots: rawContinuousEventTimeslots = [],
  } = cachedContinuousAppointments || { appointments: [], eventTimeSlots: [] };

  // Use user-specific continuous appointments if a single user is selected, otherwise use day-based
  // When a specific user is selected, we want to show their continuous appointments regardless of continuousView mode
  const finalContinuousAppointments =
    selectedUserId &&
    cachedContinuousAppointmentsForUser &&
    cachedContinuousAppointmentsForUser.appointments.length > 0
      ? cachedContinuousAppointmentsForUser.appointments
      : rawContinuousAppointments;

  const finalContinuousEventTimeslots =
    selectedUserId &&
    cachedContinuousAppointmentsForUser &&
    cachedContinuousAppointmentsForUser.eventTimeSlots.length > 0
      ? cachedContinuousAppointmentsForUser.eventTimeSlots
      : rawContinuousEventTimeslots;

  // Merge group patients into therapist appointments for continuous appointments (same as regular)
  const mergedContinuousAppointments = useMemo(
    () => mergeGroupPatientIntoContinuousTherapistAppointments(finalContinuousAppointments),
    [finalContinuousAppointments]
  );

  // Stabilize continuous arrays (shallow)
  const continuousAppointments = useShallowStableArray(mergedContinuousAppointments);
  const continuousEventTimeslots = useShallowStableArray(finalContinuousEventTimeslots);

  const drawerWidth = 320;
  const headerWidth = patientDrawerOpen ? `calc(100% - ${drawerWidth}px)` : "100%";
  // Return data and handlers
  // Stabilize frequently changing query array refs (shallow structural sharing)
  const patientAvailabilitiesStable = useShallowStableArray(patientUnavailabilityData);
  const patientUnavailableTypesStable = useShallowStableArray(patientUnavailabilityTypesData);

  // Fetch sublocalities via shared query hook
  const { data: sublocalities = EMPTY_SUBLOCALITIES } = useSublocalitiesForAppointmentsAddresses(
    continuousView?continuousAppointments:filteredAppointments,
    !!locationId
  );

  // Generate resources from users using the controller-based approach
  const resources = useMemo(() => {
    if (!fetchedUsers.length) {
      // console.debug("[CalendarController] resources: No fetchedUsers, returning []");
      return [];
    }

    // Filter users based on columnUserIds if provided (for column selection).
    // An EMPTY columnUserIds means two different things depending on the mode:
    // in role mode the role-by-date query owns the list, so empty is a real
    // "nobody holds a work plan here on this date" and must render no columns —
    // reading it as "show everyone" put the whole team on dates none of them
    // worked. Outside role mode, empty still means "no manual selection → all".
    const filteredUsers =
      columnUserIds && columnUserIds.length > 0
        ? fetchedUsers.filter((user) => columnUserIds.includes(user.id!))
        : isRoleMode
          ? []
          : fetchedUsers;

    // console.debug("[CalendarController] resources calculation:", {
      // fetchedUsersCount: fetchedUsers.length,
      // columnUserIdsCount: columnUserIds?.length || 0,
      // filteredUsersCount: filteredUsers.length,
      // columnUserIds: columnUserIds?.slice(0, 5), // show first 5
    // });

    // Calculate warning count for each user (all work-related timeslots outside working hours)
    // Includes: appointments, events, travel time, continuous appointments
    // Excludes: vacation/sick leave, lunch breaks, cancelled days
    const warningCountMap = new Map<string, number>();
    
    // Use continuousView flag to determine which appointments to count
    // Dauertermin: only continuous appointments
    // Hauptkalender: only regular appointments, events, and travel times
    if (continuousView && continuousWorkPlansForUsers) {
      // Dauertermin mode: count continuous appointments against work plan hours
      // Use time-only comparison because work plan hours are projected to horizon week,
      // not the selected calendar date
      const selectedDayOfWeek = selectedDate.getDay();
      filteredUsers.forEach(user => {
        const userId = user.id!;
        const userWorkingHours = continuousWorkPlansForUsers.filter(wh => wh.userId === userId && !wh.isBreak);
        
        let warningCount = 0;
        
        // Count continuous appointments (only those matching the selected day of week,
        // since continuousAppointments may include all weekdays when a single user is selected)
        if (continuousAppointments) {
          continuousAppointments.forEach((appointment: any) => {
            const hasThisUser = appointment.timeSlots?.some((ts: any) => ts.user?.id === userId);
            if (hasThisUser && appointment.start && appointment.end
                && new Date(appointment.start).getDay() === selectedDayOfWeek) {
              const isOutside = isTimeOutsideContinuousWorkingHours(
                new Date(appointment.start),
                new Date(appointment.end),
                userWorkingHours
              );
              if (isOutside) warningCount++;
            }
          });
        }
        
        // Count continuous event timeslots
        if (continuousEventTimeslots) {
          continuousEventTimeslots.forEach((event: any) => {
            if (event.user?.id === userId && event.start && event.end
                && new Date(event.start).getDay() === selectedDayOfWeek) {
              const isOutside = isTimeOutsideContinuousWorkingHours(
                new Date(event.start),
                new Date(event.end),
                userWorkingHours
              );
              if (isOutside) warningCount++;
            }
          });
        }
        
        if (warningCount > 0) {
          warningCountMap.set(userId, warningCount);
        }
      });
    } else if (derivedWorkingHours) {
      // Hauptkalender mode: only count regular appointments
      const selectedDateStr2 = moment(selectedDate).format('YYYY-MM-DD');
      filteredUsers.forEach(user => {
        const userId = user.id!;
        // Filter working hours to selected date only (derivedWorkingHours may span a whole week)
        const userWorkingHours = derivedWorkingHours.filter(wh => 
          wh.userId === userId && !wh.isBreak && wh.date === selectedDateStr2
        );
        
        let warningCount = 0;
        
        // Count regular appointments (only those on the selected date to avoid stale data mismatch)
        if (filteredAppointments) {
          filteredAppointments.forEach((appointment: AppointmentDto) => {
            const hasThisUser = appointment.timeSlots?.some((ts: TimeSlotDto) => ts.user?.id === userId);
            if (hasThisUser && appointment.start && appointment.end
                && moment(appointment.start).format('YYYY-MM-DD') === selectedDateStr2) {
              const isOutside = isAppointmentOutsideWorkingHours(
                new Date(appointment.start),
                new Date(appointment.end),
                userWorkingHours
              );
              if (isOutside) warningCount++;
            }
          });
        }
        
        // Manually-created events (EventTimeSlot) are not working time and are
        // intentionally NOT counted as outside-working-hours warnings.
        
        // Count travel times
        if (travelTimes) {
          travelTimes.forEach((travel: any) => {
            if (travel.user?.id === userId && travel.start && travel.end
                && moment(travel.start).format('YYYY-MM-DD') === selectedDateStr2) {
              const isOutside = isAppointmentOutsideWorkingHours(
                new Date(travel.start),
                new Date(travel.end),
                userWorkingHours
              );
              if (isOutside) warningCount++;
            }
          });
        }
        
        if (warningCount > 0) {
          warningCountMap.set(userId, warningCount);
        }
      });
    }

    return toCalendarResources(filteredUsers, {
      selectedDate,
      isRouteplanView: false, // TODO: get from store if needed
      routeplanUser: undefined, // TODO: get from store if needed
      selectedUser: undefined, // TODO: get from store if needed
      patientAvailabilities: patientAvailabilitiesStable, // Use first availability for now
      patientUnavailableTypes: patientUnavailableTypesStable,
      derivedWorkingHours,
      warningCountMap,
      continuousWorkPlansForUsers: continuousWorkPlansForUsers,
      isContinuous: continuousView,
    });
  }, [
    fetchedUsers,
    columnUserIds,
    isRoleMode,
    selectedDate,
    patientAvailabilitiesStable,
    patientUnavailableTypesStable,
    derivedWorkingHours,
    filteredAppointments,
    eventTimeSlots,
    travelTimes,
    continuousAppointments,
    continuousEventTimeslots,
    continuousWorkPlansForUsers,
    continuousView,
  ]);

  // Create a Set of resource IDs for fast lookup
  const resourceIds = useMemo(() => {
    return new Set(resources.map((r) => r.id));
  }, [resources]);

  // convert working hours to lunch breaks, filtered to only include users with visible resources
  const lunchBreaks = useMemo(() => {
    const breaks = derivedWorkingHours?.filter((wh) => wh.isBreak) || [];
    // Only include lunch breaks for users that have a corresponding resource
    // This prevents FullCalendar from filtering out events with non-existent resourceIds
    if (resourceIds.size > 0) {
      const filtered = breaks.filter((lb) => resourceIds.has(lb.userId));
      // console.debug("[CalendarController] lunchBreaks:", {
        // totalBreaks: breaks.length,
        // resourceIdsCount: resourceIds.size,
        // filteredBreaks: filtered.length,
      // });
      return filtered;
    }
    // console.debug("[CalendarController] lunchBreaks: no resourceIds, returning all", breaks.length);
    return breaks;
  }, [derivedWorkingHours, resourceIds, isCancelledView]);

  // convert working hours to lunch breaks, filtered to only include users with visible resources
  const continuousLunchBreaks = useMemo(() => {
    const breaks = continuousWorkPlansForUsers?.filter((wh) => wh.isBreak) || [];
    // Only include lunch breaks for users that have a corresponding resource
    // This prevents FullCalendar from filtering out events with non-existent resourceIds
    if (resourceIds.size > 0) {
      const filtered = breaks.filter((lb) => resourceIds.has(lb.userId));
      // console.debug("[CalendarController] continuousLunchBreaks:", {
        // totalBreaks: breaks.length,
        // resourceIdsCount: resourceIds.size,
        // filteredBreaks: filtered.length,
      // });
      return filtered;
    }
    // console.debug(
      // "[CalendarController] continuousLunchBreaks: no resourceIds, returning all",
      // breaks.length
    // );
    return breaks;
  }, [continuousWorkPlansForUsers, resourceIds, isCancelledView]);

  // Extract cancelled days from working hours
  const cancelledDays = useMemo(() => {
    const cancelled = derivedWorkingHours?.filter((wh) => wh.source === 'cancelled-fullDay') || [];
    // Only include cancelled days for users with visible resources
    if (resourceIds.size > 0) {
      const filtered = cancelled.filter((cd) => resourceIds.has(cd.userId));
      // console.debug("[CalendarController] cancelledDays:", {
        // totalCancelled: cancelled.length,
        // resourceIdsCount: resourceIds.size,
        // filteredCancelled: filtered.length,
      // });
      return filtered;
    }
    // console.debug("[CalendarController] cancelledDays: no resourceIds, returning all", cancelled.length);
    return cancelled;
  }, [derivedWorkingHours, resourceIds]);

  // Calendar events are derived ONLY from data inputs (query caches + view mode).
  // UI state (focusPosition, headerWidth, dialogs, warnings, ...) must NOT be a
  // dependency here: FullCalendar's `events` prop is controlled, and any rebuild
  // of this array while a mutation is in flight resyncs FullCalendar to the cache
  // — if that rebuild were triggered by unrelated UI state against a stale cache,
  // the dragged event would snap back to its old position.
  const calendarEvents = useMemo(
    () => {
        // The view mode ALONE decides which event set is rendered. `isContinuous`
        // is an either/or switch inside toCalendarEvents — it does not merge, it
        // REPLACES the regular events with the Dauertermin ones. Letting a single
        // selected therapist flip it meant the Hauptkalender silently turned into
        // the Dauertermin calendar whenever exactly one column was on screen (which
        // is what any date before the second therapist's work plan starts produces),
        // and since continuous events carry `daysOfWeek`/`startTime` with no
        // `endRecur`, FullCalendar repeated them into every past matching weekday.
        const isContinuousForEvents = continuousView;
        const events = toCalendarEvents({
          appointments: filteredAppointments || [],
          leaveOfAbsences: leaveOfAbsences || [],
          eventTimeslots: eventTimeSlots || [],
          continuousAppointments: continuousAppointments || [],
          continuousEventTimeSlots: continuousEventTimeslots || [],
          lunchBreaks: lunchBreaks || [],
          publicHolidayWorkingHours: (derivedWorkingHours || []).filter(
            (wh) => wh.source === "public-holiday"
          ),
          continuousLunchBreaks:continuousLunchBreaks || [],
          cancelledDays: cancelledDays || [],
          travelTimes: travelTimes || [],
          patientBlockingSlots: patientUnavailableTypesStable,
          groupCheck: groupCheck,
          lockedAppointments: EMPTY_LOCKED_APPOINTMENTS,
          isContinuous: isContinuousForEvents, // Also show continuous when user is selected and has continuous appointments
          sublocalities,
        });

        // Mark appointments that fall outside working hours
        // Build map from derivedWorkingHours for Hauptkalender
        // For Dauertermin, we use continuousWorkPlansForUsers with time-only comparison below
        const regularWorkingHoursMap = new Map<string, WorkingHoursDto[]>();
        
        if (derivedWorkingHours) {
          derivedWorkingHours.filter(wh => !wh.isBreak).forEach(wh => {
            const userId = wh.userId!;
            if (!regularWorkingHoursMap.has(userId)) {
              regularWorkingHoursMap.set(userId, []);
            }
            regularWorkingHoursMap.get(userId)!.push(wh);
          });
        }

        // Check each work-related event (exclude breaks, leave of absence, and working hours blocks)
        // In Dauertermin mode: only check continuous appointments
        // In Hauptkalender mode: only check regular appointments
        events.forEach(event => {
          const eventType = event.extendedProps.eventType || event.extendedProps.timeSlotType;
          const timeSlotType = event.extendedProps.timeSlotType;
          
          // Exclude: LeaveOfAbsence (vacation/sick), LunchBreak, working hours blocks
          const excludedTypes = ['LeaveOfAbsence', 'LunchBreak', 'SimpleWorkingHours', 'working-hours', 'EventTimeSlot', 'PublicHoliday'];
          const isExcluded = excludedTypes.includes(eventType) || (timeSlotType && excludedTypes.includes(timeSlotType));
          const shouldCheck = eventType && !isExcluded && event.resourceId && event.start && event.end;
          
          if (shouldCheck) {
            const isContinuousEvent = event.extendedProps.isContinuous === true;
            
            // Mode-specific logic: only check events that match the current view mode
            // If we're in continuous view (Dauertermin), only check continuous events
            // If we're in regular view (Hauptkalender), only check regular events
            const shouldCheckThisEvent = isContinuousForEvents ? isContinuousEvent : !isContinuousEvent;
            
            if (shouldCheckThisEvent) {
              if (isContinuousForEvents && continuousWorkPlansForUsers) {
                // Dauertermin: compare time-only against work plan hours
                // (work plan dates are projected to horizon week, not selected date)
                const userContinuousHours = continuousWorkPlansForUsers.filter(
                  wh => wh.userId === event.resourceId && !wh.isBreak
                );
                const isOutside = isTimeOutsideContinuousWorkingHours(
                  new Date(event.start),
                  new Date(event.end),
                  userContinuousHours
                );
                event.extendedProps.isOutsideWorkingHours = isOutside;
              } else {
                // Hauptkalender: compare full dates against derived working hours
                const userWorkingHours = regularWorkingHoursMap.get(event.resourceId) || [];
                const isOutside = isAppointmentOutsideWorkingHours(
                  new Date(event.start),
                  new Date(event.end),
                  userWorkingHours
                );
                event.extendedProps.isOutsideWorkingHours = isOutside;
              }
            } else {
              // Don't check events that don't match the current view mode
              event.extendedProps.isOutsideWorkingHours = false;
            }
          } else {
            event.extendedProps.isOutsideWorkingHours = false;
          }
        });

        return events;
    },
    [
      filteredAppointments,
      leaveOfAbsences,
      eventTimeSlots,
      continuousAppointments,
      continuousEventTimeslots,
      lunchBreaks,
      continuousLunchBreaks,
      cancelledDays,
      travelTimes,
      patientUnavailableTypesStable,
      groupCheck,
      sublocalities,
      continuousView,
      derivedWorkingHours,
      continuousWorkPlansForUsers,
    ]
  );

  const data: CalendarData = useMemo(
    () => {
      return {
      appointments: filteredAppointments,
      leaveOfAbsences,
      eventTimeslots: eventTimeSlots,
      // continuousEventTimeslots,
      // continuousAppointments,
      lunchBreaks,
      continuousLunchBreaks,
      travelTimes,
      calendarEvents,
      selectedShelfAppointment: undefined,
      patientAvailabilities: patientAvailabilitiesStable,
      patientUnavailableTypes: patientUnavailableTypesStable,
      patient: undefined as any,
      patientAppointments: undefined,
      unscheduleds: [],
      shelfAppointments: [],
      shelfContinuousAppointments: [],
      isLoading: false,
      selectedDate,
      selectedDateStr,
      isContinuousView: continuousView,
      isCancelledView,
      patientDrawerOpen,
      selectedEventRxId: undefined,
      showOpenDrawerButton: false,
      lockedAppointments: EMPTY_LOCKED_APPOINTMENTS,
      sublocalities: sublocalities,
      headerWidth,
      users: fetchedUsers,
      resources,
      groupCheck,
      focusPosition,
      };
    },
    [
      calendarEvents,
      filteredAppointments,
      leaveOfAbsences,
      eventTimeSlots,
      lunchBreaks,
      continuousLunchBreaks,
      travelTimes,
      patientAvailabilitiesStable,
      patientUnavailableTypesStable,
      selectedDate,
      selectedDateStr,
      continuousView,
      isCancelledView,
      patientDrawerOpen,
      headerWidth,
      fetchedUsers,
      resources,
      groupCheck,
      sublocalities,
      focusPosition,
    ]
  );

  const onEventContentClick = useCallback(async (event: ClientTimeSlot) => {}, []);
  const onCloseNotesDialog = useCallback(() => {}, []);
  const renderCalendar = useCallback(async () => {}, []);

  const handlers: CalendarHandlers = useMemo(
    () => ({
      onDatesSet: handleDateChange,
      onContinuousViewChange: handleContinuousViewChange,
      onCancelledViewChange: handleCancelledViewChange,
      onCreateEvent,
      onChangeEvent,
      onEventReceive,
      onRemoveEvent,
      onRemoveContinuousEvent,
      onRemoveGroupTherapist,
      onMoveEventDate,
      onAddShelfEvent,
      onEventContentClick,
      onSelectedShelfEventReceive,
      onPatientTabChange,
      onTargetPatientChange,
      onCloseNotesDialog: () => {},
      onSelectShelfAppointment,
      onDataRefreshFlag,
      onOutdated,
      renderCalendar,
    }),
    [
      handleDateChange,
      handleContinuousViewChange,
      handleCancelledViewChange,
      onCreateEvent,
      onChangeEvent,
      onEventReceive,
      onRemoveEvent,
      onRemoveContinuousEvent,
      onRemoveGroupTherapist,
      onMoveEventDate,
      onAddShelfEvent,
      onEventContentClick,
      onSelectedShelfEventReceive,
      onPatientTabChange,
      onTargetPatientChange,
      onCloseNotesDialog,
      onSelectShelfAppointment,
      onDataRefreshFlag,
      onOutdated,
      renderCalendar,
    ]
  );

  return { data, handlers };
};
